import React, { useState, useEffect, useMemo } from 'react';
import { 
  getCustomers, 
  getDispatches,
  getCustomerProductMappings 
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { openPrintWindow } from '../lib/printWindow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DatePicker } from '../components/ui/date-picker';
import { toast } from 'sonner';
import { 
  Search, 
  Loader2, 
  FileText,
  Download,
  Printer,
  Filter,
  Barcode,
  Package,
  Calendar,
  FileSpreadsheet,
  RefreshCw
} from 'lucide-react';
import { format, parse, isWithinInterval, startOfDay, endOfDay } from 'date-fns';

export default function CustomerCodeTraceReport() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [reportData, setReportData] = useState([]);
  
  // Filters
  const [customerId, setCustomerId] = useState('');
  const [supplierCode, setSupplierCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Customer's supplier codes for filter
  const [customerSupplierCodes, setCustomerSupplierCodes] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [customersRes, mappingsRes] = await Promise.allSettled([
        getCustomers(),
        getCustomerProductMappings({ limit: 1000 })
      ]);
      
      setCustomers(customersRes.status === 'fulfilled' ? (customersRes.value.data || []) : []);
      setMappings(mappingsRes.status === 'fulfilled' ? (mappingsRes.value.data.mappings || []) : []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  // Update available supplier codes when customer changes
  useEffect(() => {
    if (customerId) {
      const codes = mappings
        .filter(m => m.customer_id === customerId && m.is_active)
        .map(m => m.supplier_product_code);
      setCustomerSupplierCodes([...new Set(codes)]);
      // Reset supplier code if not available for this customer
      if (supplierCode && !codes.includes(supplierCode)) {
        setSupplierCode('');
      }
    } else {
      setCustomerSupplierCodes([]);
      setSupplierCode('');
    }
  }, [customerId, mappings, supplierCode]);

  const generateReport = async () => {
    if (!customerId) {
      toast.error('Please select a customer');
      return;
    }
    if (!dateFrom || !dateTo) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setGenerating(true);
    try {
      // Load all dispatches for this customer
      const dispatchesRes = await getDispatches({ customer_id: customerId, limit: 5000 });
      const allDispatches = dispatchesRes.data || [];
      
      // Parse date range
      const fromDate = startOfDay(parse(dateFrom, 'dd/MM/yyyy', new Date()));
      const toDate = endOfDay(parse(dateTo, 'dd/MM/yyyy', new Date()));
      
      // Filter and flatten dispatch lines
      const results = [];
      
      for (const dispatch of allDispatches) {
        // Check dispatch date within range
        let dispatchDate;
        try {
          dispatchDate = dispatch.dispatch_date 
            ? parse(dispatch.dispatch_date, 'dd/MM/yyyy', new Date())
            : new Date(dispatch.created_at);
        } catch {
          continue;
        }
        
        if (!isWithinInterval(dispatchDate, { start: fromDate, end: toDate })) {
          continue;
        }
        
        // Process dispatch lines
        for (const line of (dispatch.lines || [])) {
          // Filter by supplier code if specified
          if (supplierCode && line.supplier_product_code !== supplierCode) {
            continue;
          }
          
          // Only include lines that have a supplier product code
          if (!supplierCode && !line.supplier_product_code) {
            continue;
          }
          
          results.push({
            dispatch_id: dispatch.id,
            dispatch_number: dispatch.dispatch_number,
            dispatch_date: dispatch.dispatch_date || format(new Date(dispatch.created_at), 'dd/MM/yyyy'),
            customer_name: dispatch.customer_name,
            po_number: dispatch.po_number,
            product_name: line.product_name,
            supplier_product_code: line.supplier_product_code || '-',
            batch_code: line.batch_code || '-',
            use_by_date: line.use_by_date || '-',
            quantity: line.quantity || 0,
            cases: line.cases_override || line.calculated_cases || line.cases || 0,
            weight: line.weight_override || line.total_weight || line.estimated_weight || 0
          });
        }
      }
      
      // Sort by dispatch date descending
      results.sort((a, b) => {
        const dateA = parse(a.dispatch_date, 'dd/MM/yyyy', new Date());
        const dateB = parse(b.dispatch_date, 'dd/MM/yyyy', new Date());
        return dateB - dateA;
      });
      
      setReportData(results);
      
      if (results.length === 0) {
        toast.info('No data found for the selected filters');
      } else {
        toast.success(`Found ${results.length} records`);
      }
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = [
      'Product',
      'Customer Product Code',
      'Batch Code',
      'Use By Date',
      'Dispatch Number',
      'Dispatch Date',
      'Customer',
      'PO Number',
      'Quantity',
      'Cases',
      'Weight (kg)'
    ];
    
    const rows = reportData.map(r => [
      r.product_name,
      r.supplier_product_code,
      r.batch_code,
      r.use_by_date,
      r.dispatch_number,
      r.dispatch_date,
      r.customer_name,
      r.po_number || '',
      r.quantity,
      r.cases?.toFixed(1) || '0',
      r.weight?.toFixed(2) || '0.00'
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supplier_product_trace_${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${reportData.length} records to CSV`);
  };

  // Export to Excel (simple xlsx-style CSV)
  const exportToExcel = () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = [
      'Product',
      'Customer Product Code',
      'Batch Code',
      'Use By Date',
      'Dispatch Number',
      'Dispatch Date',
      'Customer',
      'PO Number',
      'Quantity',
      'Cases',
      'Weight (kg)'
    ];
    
    const rows = reportData.map(r => [
      r.product_name,
      r.supplier_product_code,
      r.batch_code,
      r.use_by_date,
      r.dispatch_number,
      r.dispatch_date,
      r.customer_name,
      r.po_number || '',
      r.quantity,
      r.cases?.toFixed(1) || '0',
      r.weight?.toFixed(2) || '0.00'
    ]);
    
    // Tab-separated for Excel compatibility
    const content = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
    const blob = new Blob([content], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supplier_product_trace_${format(new Date(), 'dd-MM-yyyy')}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${reportData.length} records to Excel`);
  };

  // Print report
  const printReport = () => {
    if (reportData.length === 0) {
      toast.error('No data to print');
      return;
    }
    
    const customerName = customers.find(c => c.id === customerId)?.name || '';
    
    openPrintWindow(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Customer Product Trace Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 10px; }
          h1 { font-size: 16px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 15px; font-size: 11px; }
          .filters { background: #f5f5f5; padding: 8px 12px; border-radius: 4px; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
          th { background: #f5f5f5; font-weight: 600; }
          .mono { font-family: monospace; }
          .right { text-align: right; }
          .totals { margin-top: 15px; font-weight: 600; }
          @media print { @page { size: landscape; margin: 10mm; } }
        </style>
      </head>
      <body>
        <h1>Customer Product Trace Report</h1>
        <div class="filters">
          <strong>Customer:</strong> ${customerName} | 
          <strong>Date Range:</strong> ${dateFrom} to ${dateTo}
          ${supplierCode ? ` | <strong>Customer Code:</strong> ${supplierCode}` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Customer Code</th>
              <th>Batch</th>
              <th>Use By</th>
              <th>Dispatch #</th>
              <th>Date</th>
              <th class="right">Qty</th>
              <th class="right">Cases</th>
              <th class="right">Weight</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.map(r => `
              <tr>
                <td>${r.product_name}</td>
                <td class="mono">${r.supplier_product_code}</td>
                <td class="mono">${r.batch_code}</td>
                <td>${r.use_by_date}</td>
                <td class="mono">${r.dispatch_number}</td>
                <td>${r.dispatch_date}</td>
                <td class="right">${r.quantity}</td>
                <td class="right">${r.cases?.toFixed(1) || '0'}</td>
                <td class="right">${r.weight?.toFixed(2) || '0.00'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="totals">
          Total Records: ${reportData.length} |
          Total Quantity: ${reportData.reduce((sum, r) => sum + (r.quantity || 0), 0)} |
          Total Cases: ${reportData.reduce((sum, r) => sum + (r.cases || 0), 0).toFixed(1)} |
          Total Weight: ${reportData.reduce((sum, r) => sum + (r.weight || 0), 0).toFixed(2)} kg
        </div>
      </body>
      </html>
    `);
  };

  // Summary stats
  const stats = useMemo(() => {
    return {
      totalRecords: reportData.length,
      totalQuantity: reportData.reduce((sum, r) => sum + (r.quantity || 0), 0),
      totalCases: reportData.reduce((sum, r) => sum + (r.cases || 0), 0),
      totalWeight: reportData.reduce((sum, r) => sum + (r.weight || 0), 0),
      uniqueProducts: new Set(reportData.map(r => r.product_name)).size,
      uniqueBatches: new Set(reportData.filter(r => r.batch_code !== '-').map(r => r.batch_code)).size
    };
  }, [reportData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-product-trace-report">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900">Customer Product Trace Report</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Trace dispatch history by customer product codes
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Report Filters
          </CardTitle>
          <CardDescription>
            Select a customer and date range to generate the trace report
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Customer *</Label>
              <SearchableSelect
                options={customers.map(c => ({ value: c.id, label: c.name }))}
                value={customerId}
                onValueChange={setCustomerId}
                placeholder="Select customer..."
                data-testid="filter-customer"
              />
            </div>
            
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Customer Product Code</Label>
              <Select value={supplierCode || "all"} onValueChange={(val) => setSupplierCode(val === "all" ? "" : val)} disabled={!customerId}>
                <SelectTrigger data-testid="filter-supplier-code">
                  <SelectValue placeholder="All codes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All codes</SelectItem>
                  {customerSupplierCodes.map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Date From *</Label>
              <DatePicker
                value={dateFrom}
                onChange={setDateFrom}
                placeholder="Select start date"
                data-testid="filter-date-from"
              />
            </div>
            
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Date To *</Label>
              <DatePicker
                value={dateTo}
                onChange={setDateTo}
                placeholder="Select end date"
                data-testid="filter-date-to"
              />
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={generateReport} 
                disabled={generating || !customerId || !dateFrom || !dateTo}
                className="w-full"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Generate Report</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {reportData.length > 0 && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.totalRecords}</p>
                  <p className="text-xs text-zinc-500">Records</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.uniqueProducts}</p>
                  <p className="text-xs text-zinc-500">Products</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.uniqueBatches}</p>
                  <p className="text-xs text-zinc-500">Batches</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.totalQuantity}</p>
                  <p className="text-xs text-zinc-500">Total Qty</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.totalCases.toFixed(1)}</p>
                  <p className="text-xs text-zinc-500">Total Cases</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{stats.totalWeight.toFixed(2)}</p>
                  <p className="text-xs text-zinc-500">Total Kg</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Export Buttons */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">
              Showing {reportData.length} records
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportToExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={printReport} data-testid="print-report-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>

          {/* Data Table */}
          <Card>
            <CardContent className="pt-6">
              <div className="border rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow className="bg-zinc-50">
                      <TableHead className="font-semibold">Product</TableHead>
                      <TableHead className="font-semibold">Customer Code</TableHead>
                      <TableHead className="font-semibold">Batch Code</TableHead>
                      <TableHead className="font-semibold">Use By</TableHead>
                      <TableHead className="font-semibold">Dispatch #</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold text-right">Qty</TableHead>
                      <TableHead className="font-semibold text-right">Cases</TableHead>
                      <TableHead className="font-semibold text-right">Weight</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow key={`${row.dispatch_id}-${index}`}>
                        <TableCell className="font-medium">{row.product_name}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800 font-mono text-xs">
                            {row.supplier_product_code}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{row.batch_code}</TableCell>
                        <TableCell>{row.use_by_date}</TableCell>
                        <TableCell className="font-mono text-sm">{row.dispatch_number}</TableCell>
                        <TableCell>{row.dispatch_date}</TableCell>
                        <TableCell className="text-right">{row.quantity}</TableCell>
                        <TableCell className="text-right">{row.cases?.toFixed(1) || '0'}</TableCell>
                        <TableCell className="text-right">{row.weight?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Empty state */}
      {reportData.length === 0 && customerId && dateFrom && dateTo && !generating && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Barcode className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No records found for the selected filters</p>
              <p className="text-sm text-zinc-400 mt-1">
                Try adjusting your date range or customer code filter
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
